import React, { useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { observer } from "mobx-react-lite";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import CustomTypography from "../../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../../views/components/common/buttons/AppButton";
import InlineFormError from "../../../../../../views/components/common/form/InlineFormError";
import { getModernPageColors } from "../../../../../../styles/modernListingPageSx";
import { MAX_CLIENT_POS_PER_ORDER } from "../../../../../../models/Order/OrderClientPO";
import { OrderClientPOStore } from "../../../../stores/OrderClientPOStore";
import ClientPOAddForm from "./ClientPOAddForm";
import ClientPORow from "./ClientPORow";

interface ClientPOsPopupProps {
  open: boolean;
  orderId: number;
  store: OrderClientPOStore;
  onClose: () => void;
}

type PendingRemoval = number[] | "all" | null;

/** Actions → Client POs: list, add, edit and remove an order's client POs. */
const ClientPOsPopup: React.FC<ClientPOsPopupProps> = observer(
  ({ open, orderId, store, onClose }) => {
    const theme = useTheme();
    const colors = getModernPageColors(theme);
    const isDark = theme.palette.mode === "dark";
    const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
    const [pendingRemoval, setPendingRemoval] = useState<PendingRemoval>(null);

    const surface = isDark ? theme.palette.background.paper : colors.surface;
    const border = isDark ? theme.palette.divider : colors.border;
    const titleColor = isDark ? theme.palette.text.primary : colors.title;
    const subtitleColor = isDark
      ? theme.palette.text.secondary
      : colors.subtitle;

    useEffect(() => {
      if (!open) return;
      setPendingRemoval(null);
      store.resetView();
      void store.loadClientPOs();
    }, [open, store]);

    const count = store.clientPOs.length;
    const selectedCount = store.selectedIds.length;
    const pendingCount =
      pendingRemoval === "all" ? count : (pendingRemoval?.length ?? 0);

    const handleClose = () => {
      if (!store.isBusy) onClose();
    };

    const handleConfirmRemove = async () => {
      if (!pendingRemoval) return;
      const removed = await store.removeClientPOs(pendingRemoval);
      if (removed) setPendingRemoval(null);
    };

    return (
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        fullScreen={isPhone}
        sx={{ zIndex: 2000 }}
        slotProps={{
          backdrop: {
            sx: {
              backgroundColor: theme.palette.customColors.backdrop,
              backdropFilter: "blur(4px)",
            },
          },
          paper: {
            sx: {
              borderRadius: isPhone ? 0 : "16px",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              ...(!isPhone && { maxHeight: "90vh", margin: "32px" }),
              bgcolor: surface,
            },
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            px: { xs: 2, sm: 3 },
            py: { xs: 1.5, sm: 2 },
            borderBottom: `1px solid ${border}`,
            background: isDark
              ? surface
              : `linear-gradient(135deg, ${colors.surface} 0%, ${colors.surfaceMuted} 100%)`,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <CustomTypography variant="heading4" color={titleColor}>
              Client POs
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={subtitleColor}
              sx={{ mt: 0.5 }}
            >
              Order #{orderId} · {count} of {MAX_CLIENT_POS_PER_ORDER} POs
            </CustomTypography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              title="Refresh"
              aria-label="Refresh client POs"
              onClick={() => void store.refreshData()}
              disabled={store.isLoading || store.isBusy}
            >
              {store.isLoading ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
            <IconButton
              title="Close"
              aria-label="Close"
              onClick={handleClose}
              disabled={store.isBusy}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <DialogContent
          sx={{
            px: { xs: 2, sm: 3 },
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <ClientPOAddForm store={store} />

          {store.successMessage && (
            <CustomTypography
              variant="heading8"
              color={colors.success}
              sx={{
                px: 1.25,
                py: 0.75,
                borderRadius: "6px",
                bgcolor: theme.palette.customColors.successGreenLightBackground,
                border: `1px solid ${theme.palette.customColors.successGreenLightBorder}`,
              }}
            >
              {store.successMessage}
            </CustomTypography>
          )}

          <Box>
            {/* List toolbar */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexWrap: "wrap",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                {count > 0 && (
                  <Checkbox
                    size="small"
                    checked={store.allSelected}
                    indeterminate={selectedCount > 0 && !store.allSelected}
                    onChange={store.toggleSelectAll}
                    disabled={store.isBusy}
                    inputProps={{ "aria-label": "Select all POs" }}
                    sx={{ p: 0.5, ml: { xs: 0.75, sm: 1 } }}
                  />
                )}
                <CustomTypography variant="heading7" color={titleColor}>
                  Uploaded POs ({count})
                </CustomTypography>
              </Box>
              {count > 0 && (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <AppButton
                    label={`Remove selected (${selectedCount})`}
                    variant="cancel"
                    onClick={() => setPendingRemoval([...store.selectedIds])}
                    disabled={selectedCount === 0 || store.isBusy}
                    customStyles={{ height: 32, minHeight: 32, px: 1.5 }}
                  />
                  <AppButton
                    label="Remove all"
                    variant="cancel"
                    onClick={() => setPendingRemoval("all")}
                    disabled={store.isBusy}
                    customStyles={{ height: 32, minHeight: 32, px: 1.5 }}
                  />
                </Box>
              )}
            </Box>

            {/* Remove confirmation */}
            {pendingRemoval && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                  flexWrap: "wrap",
                  mb: 1,
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: `${theme.palette.error.main}14`,
                  border: `1px solid ${theme.palette.error.main}33`,
                }}
              >
                <CustomTypography
                  variant="heading8"
                  color={theme.palette.error.main}
                >
                  {pendingRemoval === "all"
                    ? `Remove all ${count} client POs from this order?`
                    : `Remove ${pendingCount} client PO${pendingCount === 1 ? "" : "s"}?`}
                </CustomTypography>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <AppButton
                    label="Keep"
                    variant="grayscale"
                    onClick={() => setPendingRemoval(null)}
                    disabled={store.isRemoving}
                    customStyles={{ height: 32, minHeight: 32, px: 1.5 }}
                  />
                  <AppButton
                    label={store.isRemoving ? "Removing..." : "Remove"}
                    variant="cancel"
                    onClick={() => void handleConfirmRemove()}
                    disabled={store.isRemoving}
                    customStyles={{ height: 32, minHeight: 32, px: 1.5 }}
                  />
                </Box>
              </Box>
            )}

            <InlineFormError message={store.removeError} />
            <InlineFormError message={store.downloadError} />
            <InlineFormError message={store.loadError} />

            {store.isLoading && count === 0 ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : count === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  minHeight: "8rem",
                  px: 2,
                  borderRadius: 2,
                  border: `1px dashed ${border}`,
                }}
              >
                <CustomTypography variant="heading6" color={subtitleColor}>
                  No client POs yet
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  color={subtitleColor}
                  sx={{ mt: 0.5 }}
                >
                  Add the first PO above.
                </CustomTypography>
              </Box>
            ) : (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {store.clientPOs.map((po, index) => (
                  <ClientPORow
                    key={po.id}
                    po={po}
                    index={index}
                    store={store}
                    onRemove={(poId) => setPendingRemoval([poId])}
                  />
                ))}
              </Box>
            )}
          </Box>
        </DialogContent>

        {/* Footer */}
        <DialogActions
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.5,
            borderTop: `1px solid ${border}`,
          }}
        >
          <AppButton
            label="Done"
            onClick={handleClose}
            disabled={store.isBusy}
            customStyles={{ px: 3 }}
          />
        </DialogActions>
      </Dialog>
    );
  },
);

export default ClientPOsPopup;
